import { Badge, RadioGroup, RadioGroupItem } from '@qodo/design-system';
import { Mail } from 'lucide-react';
import { Step } from '../../../shared/InstallationDialog';
import { cn } from '../../../utils/className';
import { spawn } from '../../../utils/translations';
import { useGitlabInstallation } from '../gitlabInstallationContext';
import { GitlabAuthType } from '../types';

const RADIO_ITEM_CLASS =
  "p-3 rounded-sm bg-fill-interactive-neutral-idle border border-transparent has-[button[aria-checked='true']]:border-border-input-emphasis-active";

export const BeforeYouBegin = () => {
  const spawnBeforeYouBegin = spawn(
    'dialogs.gitlab-installation.before-you-begin',
  );
  const spawnGeneral = spawn('general');
  const { authType, setAuthType, isTokenAuthEnabled, isGroupTokenAuthEnabled } =
    useGitlabInstallation();

  // "(recommended)" attaches to the best selectable token option: GAT if its
  // flag is on, else PAT if its flag is on, else nobody.
  const isPatRecommended = !isGroupTokenAuthEnabled && isTokenAuthEnabled;
  const isPatComingSoon = !isTokenAuthEnabled;

  const patLabelText = isPatRecommended
    ? spawnBeforeYouBegin('auth-type.pat.label-recommended')
    : spawnBeforeYouBegin('auth-type.pat.label');
  const patLabel = isPatComingSoon ? (
    <span className="flex items-center gap-2">
      {spawnBeforeYouBegin('auth-type.pat.label')}
      <Badge>{spawnGeneral('coming-soon')}</Badge>
    </span>
  ) : (
    patLabelText
  );

  return (
    <Step
      title={spawnBeforeYouBegin('title')}
      description={spawnBeforeYouBegin('auth-type.description')}
    >
      <RadioGroup
        name="gitlab-auth-type"
        value={authType}
        onValueChange={(value) => setAuthType(value as GitlabAuthType)}
        direction="vertical"
        className="gap-4 pt-2"
      >
        {isGroupTokenAuthEnabled && (
          <RadioGroupItem
            value="gat"
            name="gitlab-auth-type"
            label={spawnBeforeYouBegin('auth-type.gat.label-recommended')}
            description={spawnBeforeYouBegin('auth-type.gat.description')}
            wrapperClassName={RADIO_ITEM_CLASS}
          />
        )}
        <RadioGroupItem
          value="pat"
          name="gitlab-auth-type"
          disabled={isPatComingSoon}
          label={patLabel}
          description={spawnBeforeYouBegin('auth-type.pat.description')}
          wrapperClassName={cn(RADIO_ITEM_CLASS, {
            'pointer-events-none': isPatComingSoon,
          })}
        />
        <RadioGroupItem
          value="oauth"
          name="gitlab-auth-type"
          label={spawnBeforeYouBegin('auth-type.oauth.label')}
          description={spawnBeforeYouBegin('auth-type.oauth.description')}
          wrapperClassName={RADIO_ITEM_CLASS}
        />
      </RadioGroup>

      {authType === 'oauth' && (
        <>
          <h5 className="flex flex-row items-center gap-4 text-h5 text-text-general-neutral-mid">
            <Mail />
            <div>{spawnBeforeYouBegin('instruction')}</div>
          </h5>
          <div className="text-text-general-neutral-subtle">
            {spawnBeforeYouBegin('footer')}
          </div>
        </>
      )}
    </Step>
  );
};
